import { RPM_SNAPSHOT_ROW_FIELDS } from '@/shared/api/graphql/remotePatientMonitoring';
import type { FollowUpRemindersTypes } from '@/shared/lib/types/accountProfile';
import type { MedicalSubServiceType } from '@/shared/lib/types/clinicalCare';
import type {
  AccountType,
  ClientOrganizationType,
  FileMonitoringType,
  RelyingParty,
  RelyingPartyAdminType
} from '@/shared/lib/types/graphql';
import type Meta from '@/shared/lib/types/meta';
import type { AccountsSharingPhone, PatientsData } from '@/shared/lib/types/patients';
import type { JSONData } from '@/shared/ui/JsonKit';
import { gql } from '@apollo/client';

export const ADD_ACCOUNT = gql`
  mutation AddAccount(
    $firstName: String!
    $middleName: String
    $lastName: String!
    $email: Email
    $dateOfBirth: ISO8601Date!
    $phoneNumber: String!
    $gender: String!
    $zipcode: String!
    $healthPlan: String!
    $consent: String!
    $clientOrganization: String!
    $clientStore: String!
    $ehrId: String
    $mtmCaseNumber: String
    $addressLine1: String
    $addressLine2: String
    $addressCity: String
    $addressState: String
  ) {
    addAccount(
      firstName: $firstName
      middleName: $middleName
      lastName: $lastName
      email: $email
      dateOfBirth: $dateOfBirth
      phoneNumber: $phoneNumber
      gender: $gender
      zipcode: $zipcode
      healthPlan: $healthPlan
      consent: $consent
      clientOrganization: $clientOrganization
      clientStore: $clientStore
      ehrId: $ehrId
      mtmCaseNumber: $mtmCaseNumber
      addressLine1: $addressLine1
      addressLine2: $addressLine2
      addressCity: $addressCity
      addressState: $addressState
    ) {
      status
      message
      account {
        id
        truentityId
        rpmStatus
        doNotCall
        phone
        accountsAssignments {
          relyingPartyAdmin {
            id
            name
          }
        }
        user {
          id
          firstName
          middleName
          lastName
          verifiedEmail
        }
      }
    }
  }
`;

export type GetPatientDetailResponse = {
  accountGet: PatientsData;
};

export const GET_PATIENT_DETAIL_FOR_CALENDAR = gql`
  query getPatientDetailForCalendar($truentityId: String!) {
    accountGet(truentityId: $truentityId) {
      id
      truentityId
      address
      phone
      birthDate
      accountExternalEhr {
        type
        externalId
      }
      numOfReadingsInCurrentMonth
      totalTimeSpentSecsInCurrentMonth
      gender
      rpmStatus
      rpmApprovalStatus
      healthPlan {
        id
        orgName
      }
      emergencyContactName
      emergencyContactPhone
      emergencyContactEmail
      addressLine1
      addressLine2
      addressState
      addressCity
      zipcode
      consent
      data
      rpmStatus
      clientOrgs {
        name
        id
        clientStores {
          name
          id
        }
      }
      clientStores {
        name
        id
      }
      user {
        id
        firstName
        middleName
        lastName
        verifiedEmail
      }
      numQuickUploads
      numQuickUploadsNew
      doNotCall
      doNotCallLastSpecifiedAt
      accountsMonitoringDevices {
        id
        externalPatientId
        hardwareId
        hardwarePatientId
        status
        monitoringDevice {
          id
          brandName
          name
          typeName
        }
      }
      primaryLanguage
      numPendingTasks
      numCompletedTasks
      lastEncounterDate
      accountsAssignments {
        relyingPartyAdmin {
          id
          name
        }
      }
      currentAccountsAssignments {
        relyingPartyAdmin {
          id
          name
        }
      }
      accountsFollowupRemindersByAdmin {
        id
        followUpOn
        type
        mode
        appointmentRemindersEnabled
        subType
        notes
        status
        relyingPartyAdmin {
          id
          user {
            firstName
            lastName
            email
          }
        }
        createdAt
      }
    }
  }
`;

export const GET_PATIENT_DETAIL = gql`
  query getPatientDetail($truentityId: String!) {
    accountGet(truentityId: $truentityId) {
      id
      truentityId
      address
      phone
      smsOptOut {
        optedOutAt
        keyword
        source
      }
      smsRestartPending {
        restartedAt
        restartedByName
        truentityPhoneNumber
      }
      birthDate
      isActive
      billingHoldActive
      activeBillingHoldReasons
      visibleBillingHolds {
        reason
        label
        detail
        severity
        startedAt
        billingMonth
      }
      eligibilityReview {
        status
        reason
        payer
        lastChecked
        noInsurance
      }
      accountExternalEhr {
        type
        externalId
      }
      accountExternalData {
        dataType
        value
      }
      numOfReadingsInCurrentMonth
      totalTimeSpentSecsInCurrentMonth
      gender
      genderAvatarUrl
      rpmStatus
      rpmStatusUpdatedAt
      rpmApprovalStatus
      rpmSignOffStatus
      healthPlan {
        id
        orgName
      }
      emergencyContactName
      emergencyContactPhone
      emergencyContactEmail
      addressLine1
      addressLine2
      addressState
      addressCity
      zipcode
      accountContacts {
        id
        firstName
        middleName
        lastName
        phone
        email
        dob
        relationshipType
        isGuarantor
        isEmergencyContact
        canAccessMedicalRecords
        addresses {
          line1
          line2
          city
          state
          zipcode
        }
      }
      consent
      data
      rpmStatus
      ccmStatus
      rpmEnrolledAt
      rpmUnenrolledAt
      ccmEnrolledAt
      ccmUnenrolledAt
      onboardedAt
      clientOrgs {
        name
        id
        clientStores {
          name
          id
        }
      }
      clientStores {
        name
        id
      }
      user {
        id
        firstName
        middleName
        lastName
        email
        verifiedEmail
        emailVerifiedAt
        emailEditable
        emailEditRestriction
        pendingEmailVerification {
          id
          email
          expiresAt
          resendAvailableAt
          attemptsRemaining
        }
      }
      numQuickUploads
      numQuickUploadsNew
      doNotCall
      doNotCallLastSpecifiedAt
      lastContacted {
        type
        date
      }
      accountsFollowupReminders {
        id
        followUpOn
        type
        mode
        appointmentRemindersEnabled
        subType
        notes
        status
        relyingPartyAdmin {
          id
          user {
            firstName
            lastName
            email
          }
        }
        createdAt
      }
      accountsMonitoringDevices {
        id
        externalPatientId
        hardwareId
        hardwarePatientId
        status
        monitoringDevice {
          id
          brandName
          name
          typeName
        }
        latestReadings {
          id
          value
          unit
          recordedAt
          source
          isBaseline
        }
      }
      primaryLanguage
      numPendingTasks
      numCompletedTasks
      lastEncounterDate
      setting
      isTempVitalAccessBlocked
      isTempVitalAccessConfigured
      accountsAssignments {
        relyingPartyAdmin {
          id
          name
        }
      }
      currentAccountsAssignments {
        relyingPartyAdmin {
          id
          name
        }
      }
      accountsFollowupRemindersByAdmin {
        id
        followUpOn
        type
        mode
        appointmentRemindersEnabled
        subType
        notes
        status
        relyingPartyAdmin {
          id
          user {
            firstName
            lastName
            email
          }
        }
        createdAt
      }
      accountRpmMonitoredCondition
      accountSummary {
        isTelehealthVisitLaunched
      }
      lastCompletedFollowup {
        consultAt
        signOffAt
      }
      accountRpmDiagnosis
      rpmAlternateBillingProvider {
        id
      }
      tags {
        id
        name
        colorCode
      }
    }
  }
`;

export type GetAthenaPatientDetailResponse = {
  getAccountWithAthenaDetails: {
    homePhone: string;
    consentToCall: boolean;
    consentToText: boolean;
    contactPreference: string;
    language: string;
    race: string;
    ethnicityCode: string;
    maritalStatus: string;
    usualProviderId: string;
  };
};

export const GET_ATHENA_PATIENT_DETAIL = gql`
  query GetAccountWithAthenaDetails($truentityId: ID!) {
    getAccountWithAthenaDetails(truentityId: $truentityId) {
      homePhone
      consentToCall
      consentToText
      contactPreference
      language
      race
      ethnicityCode
      maritalStatus
      usualProviderId
      guarantorRelationship
    }
  }
`;

export type GetAthenaProvidersResponse = {
  getAthenaProviders: {
    totalCount: number;
    providers: {
      providerid: number;
      firstname: string;
      lastname: string;
      displayname: string;
    }[];
  };
};

export const GET_ATHENA_PROVIDERS = gql`
  query GetAthenaProviders {
    getAthenaProviders
  }
`;

export type CreateAccountWithAthenaSyncResponse = {
  createAccountWithAthenaSyncMutation: {
    status: string;
    message: string;
    account: {
      truentityId: string;
    };
  };
};

export const ADD_NEW_PATIENT_WITH_ATHENA = gql`
  mutation CreateAccountWithAthenaSyncMutation(
    $truentityId: String
    $firstName: String!
    $middleName: String
    $lastName: String!
    $dateOfBirth: ISO8601Date!
    $gender: String!
    $usualProviderId: String!
    $addressLine1: String!
    $addressLine2: String
    $city: String!
    $state: String!
    $zipcode: String!
    $mobilePhone: String!
    $homePhone: String!
    $contactPreference: String!
    $language: String!
    $race: String!
    $ethnicityCode: String!
    $maritalStatus: String!
  ) {
    createAccountWithAthenaSyncMutation(
      truentityId: $truentityId
      firstName: $firstName
      middleName: $middleName
      lastName: $lastName
      dateOfBirth: $dateOfBirth
      gender: $gender
      usualProviderId: $usualProviderId
      addressLine1: $addressLine1
      addressLine2: $addressLine2
      city: $city
      state: $state
      zipcode: $zipcode
      mobilePhone: $mobilePhone
      homePhone: $homePhone
      contactPreference: $contactPreference
      language: $language
      race: $race
      ethnicityCode: $ethnicityCode
      maritalStatus: $maritalStatus
    ) {
      status
      message
      account {
        truentityId
        accountExternalData {
          dataType
          value
        }
      }
    }
  }
`;

export type UpdateUserProfileWithAthenaResponse = {
  updateAccountWithAthenaSyncMutation: {
    status: string;
    message: string;
    account: {
      truentityId: string;
    };
  };
};

export const UPDATE_USER_PROFILE_WITH_ATHENA = gql`
  mutation UpdateAccountWithAthenaSyncMutation(
    $truentityId: String!
    $firstName: String
    $lastName: String
    $middleName: String
    $birthDate: ISO8601Date
    $gender: String
    $usualProviderId: String
    $addressLine1: String
    $addressLine2: String
    $city: String
    $state: String
    $zipcode: String
    $homePhone: String
    $mobilePhone: String
    $contactPreference: String
    $language: String
    $race: String
    $ethnicityCode: String
    $maritalStatus: String
    $addToAthena: Boolean
    $healthPlan: HealthPlanInput
    $emergencyContactName: String
    $emergencyContactPhone: String
    $emergencyContactEmail: String
    $doNotCall: Boolean
    $doNotCallLastSpecifiedAt: ISO8601DateTime
    $nextCallSuggestedAt: ISO8601DateTime
    $primaryLanguage: String
    $clientOrganization: String
    $clientStore: String
    $ehrId: String
    $rpmAlternateBillingProviderId: ID
    $mtmCaseNumber: String
    $guarantorRelationship: String
    $guarantorFirstName: String
    $guarantorLastName: String
    $guarantorMiddleName: String
    $guarantorDob: ISO8601Date
    $guarantorPhone: String
    $guarantorEmail: String
    $guarantorAddressLine1: String
    $guarantorAddressLine2: String
    $guarantorCity: String
    $guarantorState: String
    $guarantorZipcode: String
    $guarantorSameAsPatientAddress: Boolean
    $guarantorIsEmergencyContact: Boolean
    $guarantorCanAccessMedicalRecords: Boolean
  ) {
    updateAccountWithAthenaSyncMutation(
      truentityId: $truentityId
      firstName: $firstName
      lastName: $lastName
      middleName: $middleName
      gender: $gender
      usualProviderId: $usualProviderId
      addressLine1: $addressLine1
      addressLine2: $addressLine2
      city: $city
      state: $state
      zipcode: $zipcode
      homePhone: $homePhone
      mobilePhone: $mobilePhone
      contactPreference: $contactPreference
      language: $language
      race: $race
      ethnicityCode: $ethnicityCode
      maritalStatus: $maritalStatus
      healthPlan: $healthPlan
      doNotCall: $doNotCall
      doNotCallLastSpecifiedAt: $doNotCallLastSpecifiedAt
      ehrId: $ehrId
      addToAthena: $addToAthena
      birthDate: $birthDate
      emergencyContactName: $emergencyContactName
      emergencyContactPhone: $emergencyContactPhone
      emergencyContactEmail: $emergencyContactEmail
      nextCallSuggestedAt: $nextCallSuggestedAt
      primaryLanguage: $primaryLanguage
      clientOrganization: $clientOrganization
      clientStore: $clientStore
      rpmAlternateBillingProviderId: $rpmAlternateBillingProviderId
      mtmCaseNumber: $mtmCaseNumber
      guarantorRelationship: $guarantorRelationship
      guarantorFirstName: $guarantorFirstName
      guarantorLastName: $guarantorLastName
      guarantorMiddleName: $guarantorMiddleName
      guarantorDob: $guarantorDob
      guarantorPhone: $guarantorPhone
      guarantorEmail: $guarantorEmail
      guarantorAddressLine1: $guarantorAddressLine1
      guarantorAddressLine2: $guarantorAddressLine2
      guarantorCity: $guarantorCity
      guarantorState: $guarantorState
      guarantorZipcode: $guarantorZipcode
      guarantorSameAsPatientAddress: $guarantorSameAsPatientAddress
      guarantorIsEmergencyContact: $guarantorIsEmergencyContact
      guarantorCanAccessMedicalRecords: $guarantorCanAccessMedicalRecords
    ) {
      status
      message
      account {
        truentityId
      }
    }
  }
`;

export type GetAccountsResponse = {
  accountsByProvider: {
    accounts: PatientsData[];
    meta: Meta;
  };
};

export const GET_ACCOUNT_COMMENTS = gql`
  query comments($truentityId: String!, $pageNum: Int!, $pageSize: Int!) {
    commentsByAccount(truentityId: $truentityId, pageNum: $pageNum, pageSize: $pageSize) {
      comments {
        id
        body
        createdAt
        sender {
          name
          email
        }
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export const DELETE_ACCOUNT_COMMENT = gql`
  mutation removeCommentFromAccount($commentId: String!, $truentityId: String!) {
    removeCommentFromAccount(commentId: $commentId, truentityId: $truentityId) {
      status
      message
      comment {
        id
        body
      }
    }
  }
`;

export const GET_ACCOUNTS = gql`
  query getPatients($pageNum: Int!, $pageSize: Int!) {
    accountsByProvider(pageNum: $pageNum, pageSize: $pageSize) {
      accounts {
        id
        truentityId
        address
        phone
        birthDate
        gender
        emergencyContactName
        emergencyContactPhone
        emergencyContactEmail
        addressLine1
        addressLine2
        addressState
        addressCity
        zipcode
        consent
        data
        clientOrgs {
          name
          id
          clientStores {
            name
            id
          }
        }
        clientStores {
          name
          id
        }
        user {
          id
          firstName
          middleName
          lastName
          verifiedEmail
        }
        doNotCall
        doNotCallLastSpecifiedAt
        accountsFollowupReminders {
          id
          followUpOn
          type
          mode
          appointmentRemindersEnabled
          subType
          notes
          status
          relyingPartyAdmin {
            id
            user {
              firstName
              lastName
              email
            }
          }
          createdAt
        }
        primaryLanguage
        currentMedsCount
        lastMedrecDate
        lastImport {
          status
          lastImportDate
        }
        numQuickUploads
        numQuickUploadsNew
        numPendingTasks
        numCompletedTasks
        lastEncounterDate
        accountsAssignments {
          relyingPartyAdmin {
            id
            name
          }
        }
        tags {
          id
          name
          colorCode
        }
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export type GetAccountsSuperAdminResponse = {
  accountsAll: {
    accounts: PatientsData[];
    meta: Meta;
  };
};

export const GET_ACCOUNTS_SUPER_ADMIN = gql`
  query getPatientsSuperAdmin($pageNum: Int!, $pageSize: Int!, $filterOptions: AccountFilterOptionsInput) {
    accountsAll(pageNum: $pageNum, pageSize: $pageSize, filterOptions: $filterOptions) {
      accounts {
        id
        truentityId
        address
        phone
        birthDate
        gender
        emergencyContactName
        emergencyContactPhone
        emergencyContactEmail
        addressLine1
        addressLine2
        addressState
        addressCity
        zipcode
        consent
        data
        clientOrgs {
          name
          id
          clientStores {
            name
            id
          }
        }
        clientStores {
          name
          id
        }
        user {
          id
          firstName
          middleName
          lastName
          verifiedEmail
        }
        doNotCall
        doNotCallLastSpecifiedAt
        accountsFollowupReminders {
          id
          followUpOn
          type
          mode
          appointmentRemindersEnabled
          subType
          notes
          status
          relyingPartyAdmin {
            id
            user {
              firstName
              lastName
              email
            }
          }
          createdAt
        }
        primaryLanguage
        currentMedsCount
        lastMedrecDate
        lastImport {
          status
          lastImportDate
        }
        numQuickUploads
        numQuickUploadsNew
        numPendingTasks
        numCompletedTasks
        lastEncounterDate
        accountsAssignments {
          relyingPartyAdmin {
            id
            name
          }
        }
        tags {
          id
          name
          colorCode
        }
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export type GetMyPatientsResponse = {
  accountsAssignedToRelyingPartyAdmin: {
    accounts: PatientsData[];
    meta: Meta;
  };
};

export const GET_MY_PATIENTS = gql`
  query GetAccountsAssignedRelyingPartyAdmin($relyingPartyAdminId: ID!, $pageNum: Int!, $pageSize: Int!) {
    accountsAssignedToRelyingPartyAdmin(relyingPartyAdminId: $relyingPartyAdminId, pageNum: $pageNum, pageSize: $pageSize) {
      accounts {
        id
        truentityId
        address
        phone
        birthDate
        gender
        emergencyContactName
        emergencyContactPhone
        emergencyContactEmail
        addressLine1
        addressLine2
        addressState
        addressCity
        zipcode
        consent
        data
        clientOrgs {
          name
          id
          clientStores {
            name
            id
          }
        }
        clientStores {
          name
          id
        }
        user {
          id
          firstName
          middleName
          lastName
          email
        }
        doNotCall
        doNotCallLastSpecifiedAt
        accountsFollowupReminders {
          id
          followUpOn
          type
          mode
          appointmentRemindersEnabled
          subType
          notes
          relyingPartyAdmin {
            id
            user {
              firstName
              lastName
              email
            }
          }
          createdAt
        }
        primaryLanguage
        currentMedsCount
        lastMedrecDate
        lastImport {
          status
          lastImportDate
        }
        numQuickUploads
        numQuickUploadsNew
        numPendingTasks
        numCompletedTasks
        lastEncounterDate
        accountsAssignments {
          relyingPartyAdmin {
            name
            id
          }
        }
        tags {
          id
          name
          colorCode
        }
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export const GET_MY_PATIENTS_SIMPLIFIED = gql`
  query GetAccountsAssignedRelyingPartyAdmin($relyingPartyAdminId: ID, $pageNum: Int!, $pageSize: Int!) {
    accountsAssignedToRelyingPartyAdmin(relyingPartyAdminId: $relyingPartyAdminId, pageNum: $pageNum, pageSize: $pageSize) {
      accounts {
        id
        truentityId
        rpmStatus
        ccmStatus
        mtmStatus
        tcmStatus
        user {
          id
          firstName
          middleName
          lastName
          email
        }
        clientOrgs {
          name
          id
        }
        clientStores {
          name
          id
        }
        birthDate
        phone
        accountsFollowupReminders {
          id
          followUpOn
          type
          mode
          appointmentRemindersEnabled
          subType
          notes
          status
          relyingPartyAdmin {
            id
            user {
              firstName
              lastName
              email
            }
          }
          createdAt
        }
        currentMedsCount
        lastMedrecDate
        lastImport {
          status
          lastImportDate
        }
        numQuickUploadsNew
        tags {
          id
          name
          colorCode
        }
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export type GetRelyingPartyAdminsResponse = {
  relyingPartyAdminsAll: {
    relyingPartyAdmins: { id: string; name: string; roleType: string }[];
  };
};

export type GetRelyingPartyAdminByIdResponse = {
  relyingPartyAdminById: RelyingPartyAdminType;
};

export type GetRelyingPartyRpmReviewerResponse = {
  getRpmReviewer: RelyingPartyAdminType;
};

export const GET_RELYING_PARTY_ADMINS = gql`
  query GetRelyingPartyAdminsAll($pageNum: Int!, $pageSize: Int!) {
    relyingPartyAdminsAll(pageNum: $pageNum, pageSize: $pageSize) {
      relyingPartyAdmins {
        name
        id
        roleType
      }
    }
  }
`;

export type GetFilteredRelyingPartyAdminsResponse = {
  filteredRelyingPartyAdmins: {
    relyingPartyAdmins: { id: string; name: string; roleType: string }[];
  };
};

export const GET_FILTERED_RELYING_PARTY_ADMINS = gql`
  query GetFilteredRelyingPartyAdmins($pageNum: Int!, $pageSize: Int!, $isPrimaryRpmAdmins: Boolean, $excludedRoles: [AdminRoleTypeEnum!]) {
    filteredRelyingPartyAdmins(
      pageNum: $pageNum
      pageSize: $pageSize
      isPrimaryRpmAdmins: $isPrimaryRpmAdmins
      excludedRoles: $excludedRoles
    ) {
      relyingPartyAdmins {
        name
        id
        roleType
      }
    }
  }
`;

export type GetAccountCareMonitoringFilesResponse = {
  accountFileMonitoring: {
    monitoringFiles: FileMonitoringType[];
    meta: Meta;
  };
};

export const GET_ACCOUNT_CARE_MONITORING_FILES = gql`
  query GetAccountFileMonitoringsFiles($pageNum: Int!, $pageSize: Int!, $filterOptions: AccountCareMonitoringFilesInput!) {
    accountFileMonitoring(pageNum: $pageNum, pageSize: $pageSize, filterOptions: $filterOptions) {
      monitoringFiles {
        id
        fileName
        numRecords
        logText
        sftpEndpoint
        status
        createdAt
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export type GetGlobalAccountSearchResponse = {
  accountsSearch: {
    accounts: PatientsData[];
    meta: Meta;
  };
};

export const GET_GLOBAL_SEARCH_ACCOUNTS = gql`
  query AccountsSearch($pageNum: Int!, $pageSize: Int!, $filterOptions: AccountFilterOptionsInput!) {
    accountsSearch(pageNum: $pageNum, pageSize: $pageSize, filterOptions: $filterOptions) {
      accounts {
        id
        truentityId
        gender
        rpmStatus
        mtmStatus
        tcmStatus
        zipcode
        phone
        birthDate
        medicalServices {
          name
          text
          isSameOrg
          isOfferOrg
          isEnrolled
          onboardedRelyingParties {
            name
          }
        }
        user {
          id
          verifiedEmail
          firstName
          lastName
        }
        relyingParties {
          id
          name
        }
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export const GET_GLOBAL_SEARCH_ACCOUNTS_COUNTS = gql`
  query AccountsSearch($pageNum: Int!, $pageSize: Int!, $filterOptions: AccountFilterOptionsInput!) {
    accountsSearch(pageNum: $pageNum, pageSize: $pageSize, filterOptions: $filterOptions) {
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

/**
 * Type-ahead patient lookup - one term, no field selector.
 *
 * Kept next to `accountsSearch` above because it is deliberately *not* that query: the two
 * search the same records and are trivial to confuse. `accountsSearch` is cross-tenant by
 * design, to back the Global Lookup tab, and must never be what a keystroke reaches.
 *
 * The backend decides what a term means (id, phone, date, name, EHR id, case number) and
 * reports it back as `matchedOn`, so the dropdown can say why a row is there. `isFuzzy` marks
 * a near-miss name match, which belongs under a separate heading rather than mixed in with
 * exact hits.
 */
export type QuickPatientSearchResult = {
  truentityId: string;
  name: string | null;
  birthDate: string | null;
  phone: string | null;
  rpmStatus: string | null;
  matchedOn: string;
  isFuzzy: boolean;
  relyingPartyId?: string | null;
  relyingPartyName?: string | null;
};

export type QuickPatientSearchResponse = {
  quickAccountSearch: QuickPatientSearchResult[];
};

export const QUICK_PATIENT_SEARCH = gql`
  query QuickAccountSearch($term: String!, $limit: Int) {
    quickAccountSearch(term: $term, limit: $limit) {
      truentityId
      name
      birthDate
      phone
      rpmStatus
      matchedOn
      isFuzzy
    }
  }
`;

export type AccountSearchScope = 'CURRENT_RELYING_PARTY' | 'ALL_RELYING_PARTIES';

export type AccountSearchRelyingPartyCount = {
  relyingPartyId: string;
  name: string;
  count: number;
  isCapped: boolean;
  isCurrent: boolean;
};

export type QuickAccountLookup = {
  results: QuickPatientSearchResult[];
  relyingPartyCounts: AccountSearchRelyingPartyCount[];
  totalCount: number;
  isTotalCapped: boolean;
};

export type QuickAccountLookupResponse = {
  quickAccountLookup: QuickAccountLookup;
};

export const QUICK_ACCOUNT_LOOKUP = gql`
  query QuickAccountLookup($term: String!, $limit: Int, $scope: AccountSearchScopeEnum, $includeCounts: Boolean) {
    quickAccountLookup(term: $term, limit: $limit, scope: $scope, includeCounts: $includeCounts) {
      results {
        truentityId
        name
        birthDate
        phone
        rpmStatus
        matchedOn
        isFuzzy
        relyingPartyId
        relyingPartyName
      }
      relyingPartyCounts {
        relyingPartyId
        name
        count
        isCapped
        isCurrent
      }
      totalCount
      isTotalCapped
    }
  }
`;

export const ONBOARD_ACCOUNT = gql`
  mutation OnboardAccount($truentityId: String!, $service: String!) {
    onboardAccount(truentityId: $truentityId, service: $service) {
      status
      message
    }
  }
`;

export type GetAccountsByFilterResponse = {
  accountsLookup: {
    accounts: PatientsData[];
    meta: Meta;
  };
};
export const GET_ACCOUNT_BY_FILTER = gql`
  query GetAccountsByFilter($pageNum: Int!, $pageSize: Int!, $filterOptions: AccountFilterOptionsInput!) {
    accountsLookup(pageNum: $pageNum, pageSize: $pageSize, filterOptions: $filterOptions) {
      accounts {
        id
        truentityId
        address
        phone
        birthDate
        gender
        emergencyContactName
        emergencyContactPhone
        emergencyContactEmail
        addressLine1
        addressLine2
        addressState
        addressCity
        zipcode
        consent
        data
        clientOrgs {
          name
          id
          clientStores {
            name
            id
          }
        }
        clientStores {
          name
          id
        }
        user {
          id
          firstName
          middleName
          lastName
          verifiedEmail
        }
        doNotCall
        doNotCallLastSpecifiedAt
        accountsFollowupReminders {
          id
          followUpOn
          type
          mode
          appointmentRemindersEnabled
          subType
          notes
          status
          relyingPartyAdmin {
            id
            user {
              firstName
              lastName
              email
            }
          }
          createdAt
        }
        primaryLanguage
        currentMedsCount
        lastMedrecDate
        lastImport {
          status
          lastImportDate
        }
        numQuickUploads
        numQuickUploadsNew
        numPendingTasks
        numCompletedTasks
        lastEncounterDate
        accountsAssignments {
          relyingPartyAdmin {
            id
            name
          }
        }
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export const GET_ACCOUNT_BY_FILTER_SIMPLIFIED = gql`
  query GetAccountsByFilterSimplified(
    $pageNum: Int!
    $pageSize: Int!
    $filterOptions: AccountFilterOptionsInput!
    $relyingPartyAdminId: String
  ) {
    accountsLookup(pageNum: $pageNum, pageSize: $pageSize, filterOptions: $filterOptions, relyingPartyAdminId: $relyingPartyAdminId) {
      accounts {
        id
        truentityId
        rpmStatus
        ccmStatus
        mtmStatus
        tcmStatus
        zipcode
        accountsMonitoringDevices {
          externalPatientId
          status
        }
        user {
          id
          firstName
          middleName
          lastName
          verifiedEmail
        }
        accountsAssignments {
          relyingPartyAdmin {
            id
            name
          }
        }
        phone
        birthDate
        accountsFollowupReminders {
          id
          followUpOn
          type
          mode
          appointmentRemindersEnabled
          subType
          notes
          status
          category
          relyingPartyAdmin {
            id
            user {
              firstName
              lastName
              email
            }
          }
          createdAt
        }
        currentMedsCount
        lastMedrecDate
        lastImport {
          status
          lastImportDate
        }
        healthPlan {
          id
          orgName
        }
        tags {
          id
          name
          colorCode
        }
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export const GET_ACCOUNT_BY_FILTER_COUNTS = gql`
  query GetAccountsByFilterCounts(
    $pageNum: Int!
    $pageSize: Int!
    $filterOptions: AccountFilterOptionsInput!
    $relyingPartyAdminId: String
  ) {
    accountsLookup(pageNum: $pageNum, pageSize: $pageSize, filterOptions: $filterOptions, relyingPartyAdminId: $relyingPartyAdminId) {
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export type GetAccountsLookupResponse = {
  accountsLookupHistory: {
    accounts: PatientsData[];
    meta: Meta;
  };
};
export const GET_ACCOUNTS_LOOKUP_HISTORY = gql`
  query GetAccountsLookupHistory($pageNum: Int!, $pageSize: Int!) {
    accountsLookupHistory(pageNum: $pageNum, pageSize: $pageSize) {
      accounts {
        id
        truentityId
        address
        phone
        birthDate
        gender
        emergencyContactName
        emergencyContactPhone
        emergencyContactEmail
        addressLine1
        addressLine2
        addressState
        addressCity
        zipcode
        consent
        data
        clientOrgs {
          name
          id
          clientStores {
            name
            id
          }
        }
        clientStores {
          name
          id
        }
        user {
          id
          firstName
          middleName
          lastName
          verifiedEmail
        }
        doNotCall
        doNotCallLastSpecifiedAt
        accountsFollowupReminders {
          id
          followUpOn
          type
          mode
          appointmentRemindersEnabled
          subType
          notes
          status
          relyingPartyAdmin {
            id
            user {
              firstName
              lastName
              email
            }
          }
          createdAt
        }
        primaryLanguage
        currentMedsCount
        lastMedrecDate
        lastImport {
          status
          lastImportDate
        }
        numQuickUploads
        numQuickUploadsNew
        numPendingTasks
        numCompletedTasks
        lastEncounterDate
        accountsAssignments {
          relyingPartyAdmin {
            id
            name
          }
        }
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export type UpdateUserProfileResponse = {
  updateUserAccountProfile: {
    account: AccountType;
  };
};

export const UPDATE_USER_PROFILE = gql`
  mutation updateUserAccountProfile($truentityId: String!, $profileData: UserAccountProfileInput!) {
    updateUserAccountProfile(truentityId: $truentityId, profileData: $profileData) {
      account {
        truentityId
        addressLine1
        addressLine2
        addressState
        addressCity
        zipcode
        healthPlan {
          id
        }
        accountExternalEhr {
          type
          externalId
        }
        phone
        birthDate
        gender
        emergencyContactName
        emergencyContactPhone
        emergencyContactEmail
        user {
          id
          firstName
          middleName
          lastName
        }
        doNotCall
        doNotCallLastSpecifiedAt
        accountsFollowupReminders {
          id
          followUpOn
          type
          mode
          appointmentRemindersEnabled
          subType
          notes
          status
          relyingPartyAdmin {
            id
            user {
              firstName
              lastName
              email
            }
          }
          createdAt
        }
        primaryLanguage
        consent
      }
    }
  }
`;

export type RolloutGroupResponse = {
  relyingParty: {
    id: string;
    rolloutGroup?: string;
  };
};

/**
 * Deliberately its own query rather than a field on LOGGED_USER.
 *
 * `currentLoggedRelyingParty` is persisted to localStorage and LOGGED_USER only runs at login,
 * 2FA and company switch -- so a field hung off the relying party would be cached across sessions
 * and never re-read on a plain reload, which is precisely what defeats a rollback. Fetched on its
 * own with `network-only`, it is re-read on every boot and never persisted.
 *
 * Keeping it separate also means a schema mismatch during the backend/web deploy window degrades
 * the rollout prompt rather than breaking login.
 */
export const ROLLOUT_GROUP = gql`
  query rolloutGroup($relyingPartyId: ID!) {
    relyingParty(relyingPartyId: $relyingPartyId) {
      id
      rolloutGroup
    }
  }
`;

export type UpdateRolloutGroupResponse = {
  updateRolloutGroup: {
    status: string;
    message?: string;
    relyingParty?: {
      id: string;
      rolloutGroup?: string | null;
    };
  };
};

/**
 * The supported way to move a pharmacy between cohorts.
 *
 * Not `updateRelyingParty`: that replaces the settings blob wholesale, so it can drop a company's
 * other feature flags, and it writes no audit entry. This merges just the one key, rejects a value
 * outside the enum at write time, and is super-admin only -- gate the UI to match rather than
 * letting the request fail.
 *
 * Re-applying the group a company is already in succeeds rather than erroring; the response
 * message says nothing was written.
 */
export const UPDATE_ROLLOUT_GROUP = gql`
  mutation updateRolloutGroup($relyingPartyId: ID!, $rolloutGroup: String!) {
    updateRolloutGroup(relyingPartyId: $relyingPartyId, rolloutGroup: $rolloutGroup) {
      status
      message
      relyingParty {
        id
        rolloutGroup
      }
    }
  }
`;

/** The pharmacy's current mailing address. The logged-in user's copy is a login-time snapshot and can be stale. */
export const GET_RELYING_PARTY_ADDRESS = gql`
  query getRelyingPartyAddress($relyingPartyId: ID!) {
    relyingParty(relyingPartyId: $relyingPartyId) {
      id
      addressLine1
      addressLine2
      addressCity
      addressState
      addressZipcode
    }
  }
`;

export type GetRelyingPartyAddressResponse = {
  relyingParty: Pick<RelyingParty, 'id' | 'addressLine1' | 'addressLine2' | 'addressCity' | 'addressState' | 'addressZipcode'> | null;
};

export const LOGGED_USER = gql`
  query loggedUser($relyingPartyId: ID!) {
    relyingParty(relyingPartyId: $relyingPartyId) {
      id
      name
      logoUrl
      orgHandle
      email
      settings
      coverLetterBody
      coverLetterS3Key
      logo
      addressLine1
      addressLine2
      addressState
      addressCity
      addressZipcode
      phone
      isCtaAccepted
      ctaAcceptedOn
      ctaAcceptedInfo
      ctaRejectedOn
      ctaAcceptedByAdmin {
        name
      }
      isMock
      medicalServices {
        shortName
      }
    }
  }
`;

export const LOGIN_TENANT_ADMIN = gql`
  mutation relyingPartyLogin($email: Email!, $password: String!, $pinCode: String, $deviceTrustToken: String) {
    relyingPartyLogin(email: $email, password: $password, pinCode: $pinCode, deviceTrustToken: $deviceTrustToken) {
      accessToken
      refreshToken
      relyingPartyAdmin {
        id
        roleType
        presignedAvatarUrl
        isSecurityPinSet
        contactsRelyingPartyAdmins {
          contactType
          value
          relyingPartyAdminId
        }
        title
        hasMultipleRelyingParties
        businessName
        addressLine1
        addressLine2
        addressCity
        addressState
        addressZipcode
        phone
        alternateEmail
        hourlyRate
        supervisorEmail
        bankName
        bankRoutingNumber
        bankAccountNumber
        externalDialpadId
        settings
        preferences
        relyingParty {
          id
          name
          logoUrl
          orgHandle
          settings
          addressLine1
          addressLine2
          addressState
          addressCity
          addressZipcode
          phone
          email
          phone
          coverLetterBody
          medicalServices {
            name
            shortName
            serviceType
          }
          isCtaAccepted
          isMock
          primaryProvider {
            name
          }
        }
        roleType
        npiNumber
        techLicense
        user {
          id
          firstName
          lastName
          email
        }
      }
      otpId
      pinRequired
    }
  }
`;

export const TWOFACTOR_LOGIN_ADMIN = gql`
  mutation relyingPartyTwofactorLogin($otpId: String!, $otpCode: String!, $rememberDevice: Boolean, $deviceTrustToken: String) {
    relyingPartyTwofactorLogin(otpId: $otpId, otpCode: $otpCode, rememberDevice: $rememberDevice, deviceTrustToken: $deviceTrustToken) {
      accessToken
      refreshToken
      deviceTrustToken
      relyingPartyAdmin {
        id
        roleType
        isSecurityPinSet
        contactsRelyingPartyAdmins {
          contactType
          value
          relyingPartyAdminId
        }
        title
        businessName
        addressLine1
        addressLine2
        addressCity
        addressState
        addressZipcode
        phone
        alternateEmail
        hourlyRate
        bankName
        bankRoutingNumber
        bankAccountNumber
        externalDialpadId
        settings
        preferences
        hasMultipleRelyingParties
        relyingParty {
          id
          name
          logoUrl
          orgHandle
          settings
          addressLine1
          addressLine2
          addressState
          addressCity
          addressZipcode
          phone
          email
          medicalServices {
            name
            shortName
            serviceType
          }
          isCtaAccepted
          isMock
        }
        roleType
        user {
          id
          firstName
          lastName
          email
        }
      }
    }
  }
`;

export type UpdateRelyingPartyResponse = {
  updateRelyingParty: {
    status: string;
    relyingParty: RelyingParty;
  };
};

export const UPDATE_RELYING_PARTY = gql`
  mutation UpdateRelyingParty(
    $relyingPartyId: ID!
    $coverLetterBody: String
    $name: String
    $logo: String
    $addressLine1: String
    $addressLine2: String
    $addressCity: String
    $addressState: String
    $addressZipcode: String
    $email: String
    $phone: String
    $settings: String
    $timezone: String
    $orgHandle: String
    $goLiveDate: ISO8601Date
    $primarySupportAdminId: ID
    $secondarySupportAdminId: ID
    $directorSupportAdminId: ID
    $isMock: Boolean
  ) {
    updateRelyingParty(
      relyingPartyId: $relyingPartyId
      name: $name
      logo: $logo
      coverLetterBody: $coverLetterBody
      addressLine1: $addressLine1
      addressLine2: $addressLine2
      addressCity: $addressCity
      addressState: $addressState
      addressZipcode: $addressZipcode
      email: $email
      phone: $phone
      settings: $settings
      timezone: $timezone
      orgHandle: $orgHandle
      goLiveDate: $goLiveDate
      primarySupportAdminId: $primarySupportAdminId
      secondarySupportAdminId: $secondarySupportAdminId
      directorSupportAdminId: $directorSupportAdminId
      isMock: $isMock
    ) {
      status
      relyingParty {
        id
        name
        isMock
        coverLetterBody
        addressLine1
        addressLine2
        addressCity
        addressState
        addressZipcode
        email
        phone
        settings
        orgHandle
        goLiveDate
        supportAdmins {
          supportRoleLevel
          relyingPartyAdmin {
            id
            user {
              firstName
              lastName
            }
          }
        }
      }
    }
  }
`;

export const UPDATE_RELYING_PARTY_PROFILE = gql`
  mutation UpdateRelyingPartyAdminProfile($relyingPartyAdminId: ID!, $profileData: RelyingPartyAdminProfileInput!) {
    updateRelyingPartyAdminProfile(relyingPartyAdminId: $relyingPartyAdminId, profileData: $profileData) {
      status
      message
    }
  }
`;

export type PioneerRxEventTypes = {
  id: string;
  accountId: string;
  account: AccountType;
  clientOrgId: string;
  clientOrg: ClientOrganizationType;
  eventName: string;
  messageHeader: string;
  messageBody: string;
  createdAt: Date;
  displayName: string;
};

export type GetAllPioneerEventsResponse = {
  getPioneerrx: {
    accounts: PioneerRxEventTypes[];
  };
};

export const GET_ALL_PIONEER_RX_EVENTS = gql`
  query GetPioneerrx($truentityId: String!) {
    getPioneerrx(truentityId: $truentityId) {
      accounts {
        id
        accountId
        account {
          truentityId
        }
        clientOrgId
        eventName
        messageHeader
        messageBody
        createdAt
      }
    }
  }
`;

export type GetPioneerEventsResponseById = {
  getPioneerrxById: PioneerRxEventTypes;
};

export const GET_PIONEER_RX_EVENT_BY_ID = gql`
  query GetPioneerrxById($truentityId: String!, $eventId: String!) {
    getPioneerrxById(truentityId: $truentityId, eventId: $eventId) {
      accountId
      clientOrgId
      clientOrg {
        id
        name
      }
      eventName
      messageBody
      messageHeader
      createdAt
    }
  }
`;

export type GetAllPioneerEventsDefaultResponse = {
  pioneerrxEvents: {
    accounts: PioneerRxEventTypes[];
  };
};

export const GET_ALL_PIONEER_RX_EVENTS_DEFAULT = gql`
  query PioneerrxEventsQuery($orgId: String!, $pageNum: Int!, $pageSize: Int!) {
    pioneerrxEvents(orgId: $orgId, pageNum: $pageNum, pageSize: $pageSize) {
      accounts {
        id
        account {
          id
          user {
            firstName
            lastName
            verifiedEmail
          }
          accountsAssignments {
            relyingPartyAdmin {
              id
              name
            }
          }
          truentityId
          gender
          phone
          birthDate
          address
          healthPlan {
            id
            orgName
          }
          currentMedsCount
        }
        clientOrg {
          id
        }
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export enum FollowUpCategoryTypes {
  GENERAL = 'GENERAL',
  CHART_REVIEW = 'CHART_REVIEW',
  ALERT_REVIEW = 'ALERT_REVIEW',
  // Program-agnostic on purpose: the consent being obtained is named by sub_type. Mirrors
  // AccountFollowupCategoryType::OBTAIN_CONSENT.
  OBTAIN_CONSENT = 'OBTAIN_CONSENT'
}

export enum FollowUpStatusTypes {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED'
}

export enum FollowUpTypes {
  CALL_PATIENT = 'Call Patient',
  CALL_PROVIDER = 'Call Provider',
  TEXT_PATIENT = 'Text Patient',
  REVIEW_CHART = 'Review Chart',
  REVIEW = 'Review',
  RPM_INITIAL_VISIT = 'RPM Initial Visit',
  FOLLOW_UP_VISIT = 'Follow-up Visit',
  PHARMACY_VISIT = 'Pharmacy Visit',
  CCM_INITIAL_VISIT = 'CCM Visit'
}

export enum FollowUpTaskTypes {
  CONTACT_PRIMARY_CARE_PROVIDER = 'Contact Primary Care Provider',
  CONTACT_PATIENT = 'Contact Patient',
  CONTINUE_CURRENT_MONITORING_AND_CATE_PLAN = 'Continue current monitoring and care plan',
  CONTACT_CARE_TEAM_MEMBER = 'Contact care team member',
  NO_INTERVENTION_REQUIRED = 'No intervention required'
}

export enum FollowUpSubTypes {
  NONE = 'None',
  RPM = 'RPM',
  CMR = 'CMR',
  TIP = 'TIP',
  Adherence = 'Adherence'
}

export type AccountsFollowupResponse = {
  addAccountFollowup?: { status: string; message: string; followUps?: FollowUpRemindersTypes[] };
  addAccountsFollowups?: { status: string; message: string; followUps?: FollowUpRemindersTypes[] };
  updateAccountFollowup?: { status: string; message: string; followUps?: FollowUpRemindersTypes[] };
};

export const ADD_ACCOUNT_FOLLOWUP = gql`
  mutation AddAccountFollowup(
    $truentityId: String!
    $followUpAssigneeIds: [ID!]!
    $followUpOn: ISO8601DateTime!
    $type: String!
    $subType: String
    $note: String
    $status: AccountFollowupStatusTypeEnum
    $category: AccountFollowupCategoryTypeEnum
    $rpmAccountReviewNoteId: ID
    $sendTextReminder: Boolean
    $textReminderMessage: String
    $mode: VisitModeTypeEnum
    $appointmentRemindersEnabled: Boolean
    $textMessagingEnabled: Boolean
  ) {
    addAccountFollowup(
      truentityId: $truentityId
      followUpAssigneeIds: $followUpAssigneeIds
      followUpOn: $followUpOn
      type: $type
      subType: $subType
      note: $note
      status: $status
      category: $category
      rpmAccountReviewNoteId: $rpmAccountReviewNoteId
      sendTextReminder: $sendTextReminder
      textReminderMessage: $textReminderMessage
      mode: $mode
      appointmentRemindersEnabled: $appointmentRemindersEnabled
      textMessagingEnabled: $textMessagingEnabled
    ) {
      status
      message
      followUps {
        id
        type
        followUpOn
        subType
        status
        notes
        mode
        appointmentRemindersEnabled
        relyingPartyAdmin {
          id
          user {
            firstName
            lastName
          }
        }
      }
    }
  }
`;

export const ADD_ACCOUNTS_FOLLOWUPS = gql`
  mutation AddAccountsFollowups($followUps: [AccountFollowupAdditionalInputV2!]!) {
    addAccountsFollowups(followUps: $followUps) {
      status
      message
    }
  }
`;

export type UpdateAccountFollowupResponse = {
  updateAccountFollowup: {
    status: string;
    message: string;
    followUps?: FollowUpRemindersTypes[];
  };
};

export const UPDATE_ACCOUNT_FOLLOWUP = gql`
  mutation UpdateAccountFollowup(
    $followUpIds: [ID!]!
    $followUpAssigneeIds: [ID!]!
    $followUpOn: ISO8601DateTime
    $type: String
    $subType: String
    $note: String
    $status: AccountFollowupStatusTypeEnum
    $sendTextReminder: Boolean
    $textReminderMessage: String
    $mode: VisitModeTypeEnum
    $appointmentRemindersEnabled: Boolean
    $textMessagingEnabled: Boolean
  ) {
    updateAccountFollowup(
      followUpIds: $followUpIds
      followUpAssigneeIds: $followUpAssigneeIds
      followUpOn: $followUpOn
      type: $type
      subType: $subType
      note: $note
      status: $status
      sendTextReminder: $sendTextReminder
      textReminderMessage: $textReminderMessage
      mode: $mode
      appointmentRemindersEnabled: $appointmentRemindersEnabled
      textMessagingEnabled: $textMessagingEnabled
    ) {
      status
      message
      followUps {
        id
        type
        followUpOn
        subType
        status
        notes
        mode
        appointmentRemindersEnabled
        relyingPartyAdmin {
          id
          user {
            firstName
            lastName
          }
        }
      }
    }
  }
`;

export type GetAccountFollowupByIdResponse = {
  accountsFollowupReminderById: FollowUpRemindersTypes;
};

export const GET_ACCOUNT_FOLLOWUP_BY_ID = gql`
  query GetAccountFollowupById($id: ID!) {
    accountsFollowupReminderById(id: $id) {
      id
      followUpOn
      type
      mode
      appointmentRemindersEnabled
      subType
      notes
      status
      category
      relyingPartyAdmin {
        id
        user {
          firstName
          lastName
          email
        }
      }
      rpmAlert {
        id
        label
        reading
        recordedAt
        description
      }
      createdByRelyingPartyAdmin {
        user {
          firstName
          lastName
        }
      }
      account {
        truentityId
        rpmEnrolledAt
        user {
          firstName
          lastName
        }
      }
      createdAt
      immediateActions
      rpmAccountReviewNote {
        note
        createdAt
        rpmAccountReviewWorkflow {
          rpmReport {
            monthYear
          }
        }
      }
    }
  }
`;

export type DeleteFollowupResponseType = {
  removeAccountFollowup: {
    status: string;
    message: string;
  };
};

export const DELETE_ACCOUNT_FOLLOWUP = gql`
  mutation RemoveAccountFollowup($followUpIds: [ID!]!, $sendTextReminder: Boolean, $textReminderMessage: String) {
    removeAccountFollowup(followUpIds: $followUpIds, sendTextReminder: $sendTextReminder, textReminderMessage: $textReminderMessage) {
      status
      message
    }
  }
`;

export type RemoveAccountFollowupsResponse = {
  removeAccountFollowups: {
    status: string;
    message: string;
  };
};

export const REMOVE_ACCOUNT_FOLLOWUPS = gql`
  mutation RemoveAccountFollowups($truentityId: String!) {
    removeAccountFollowups(truentityId: $truentityId) {
      status
      message
    }
  }
`;

export const GET_ACCOUNT_MEDICAL_SERVICES = gql`
  query GetAccountMedicalServices($truentityId: ID!, $clientOrgId: String!, $clientStoreId: String!) {
    getAccountMedicalServices(truentityId: $truentityId, clientOrgId: $clientOrgId, clientStoreId: $clientStoreId) {
      mtm
      tcm
      rpm
      map
      ms
      bloodGlucose
      bloodPressure
      weight
    }
  }
`;

export const CREATE_ACCOUNT_MEDICAL_SERVICES = gql`
  mutation CreateAccountMedicalService(
    $truentityId: ID!
    $clientOrgId: String!
    $clientStoreId: String!
    $medicalServiceOptions: MedicalServicesOptionsInput!
  ) {
    createAccountMedicalService(
      truentityId: $truentityId
      clientOrgId: $clientOrgId
      clientStoreId: $clientStoreId
      medicalServiceOptions: $medicalServiceOptions
    ) {
      status
      message
    }
  }
`;

export type GetGlobalAccountByIdResponse = {
  globalAccountById: AccountType;
};

export const GET_GLOBAL_ACCOUNT_BY_ID = gql`
  query GlobalAccountByIdQuery($truentityId: String!) {
    globalAccountById(truentityId: $truentityId) {
      truentityId
      zipcode
      address
      totalTimeSpentSecsInCurrentMonth
      consent
      birthDate
      phone
      rpmApprovalStatus
      rpmStatus
      healthPlan {
        id
        orgName
      }
      gender
      data
      setting
      user {
        id
        email
        verifiedEmail
        emailVerifiedAt
        emailEditable
        emailEditRestriction
        pendingEmailVerification {
          id
          email
          expiresAt
          resendAvailableAt
          attemptsRemaining
        }
        firstName
        lastName
      }
      clientOrgs {
        id
        name
        clientStores {
          id
          name
        }
      }
      clientStores {
        id
        name
      }
      accountsAssignments {
        relyingPartyAdmin {
          name
        }
      }
      lastImport {
        lastImportDate
        status
      }
      numPendingTasks
      numCompletedTasks
      lastEncounterDate
      doNotCall
      doNotCallLastSpecifiedAt
      nextCallSuggestedAt
      primaryLanguage
      currentMedsCount
      lastMedrecDate
      numQuickUploads
      numQuickUploadsNew
      addressLine1
      addressLine2
      addressState
      addressCity
      accountsFollowupReminders {
        id
        relyingPartyAdminId
      }
      accountsFollowupRemindersByAdmin {
        id
        relyingPartyAdminId
      }
    }
  }
`;

export type GetAccountFollowupsResponse = {
  accountFollowups: { accountFollowups: FollowUpRemindersTypes[] };
};

export const GET_ACCOUNT_FOLLOWUPS = gql`
  query AccountFollowupsQuery($truentityId: ID!, $pageNum: Int, $pageSize: Int, $filterOptions: AccountFollowupFilterOptionsInput) {
    accountFollowups(truentityId: $truentityId, pageNum: $pageNum, pageSize: $pageSize, filterOptions: $filterOptions) {
      accountFollowups {
        id
        followUpOn
        type
        mode
        appointmentRemindersEnabled
        subType
        status
        notes
        category
        account {
          truentityId
          rpmStatus
          user {
            firstName
            middleName
            lastName
          }
        }
        relyingPartyAdmin {
          id
          name
          roleType
          user {
            firstName
            middleName
            lastName
          }
        }
        rpmFollowupConsult {
          id
          consultAt
          status
        }
        clinicalCareEncounter {
          id
          startedAt
          encounterMode
          status
        }
        createdAt
        immediateActions
      }
    }
  }
`;

export type UpdateAccountSettingResponse = {
  updateAccountSetting: {
    status: string;
    message: string;
  };
};

export const UPDATE_ACCOUNT_SETTING = gql`
  mutation UpdateAccountSetting($truentityId: String!, $settings: String, $phone: String) {
    updateAccountSetting(truentityId: $truentityId, settings: $settings, phone: $phone) {
      status
      message
    }
  }
`;

export const SET_ACCOUNT_TEMP_VITAL_ACCESS_PIN = gql`
  mutation UpdateAccountTempVitalAccessPin($truentityId: ID!, $pinCode: String!) {
    updateAccountTempVitalAccessPin(truentityId: $truentityId, pinCode: $pinCode) {
      status
      message
    }
  }
`;

export type RefreshEnrolledAccountsSnapshotResponse = {
  refreshCurrentMonthEnrolledAccountsSnapshot: {
    status: string;
    message: string;
  };
};

export const REFRESH_ENROLLED_ACCOUNTS_SNAPSHOT = gql`
  mutation RefreshCurrentMonthEnrolledAccountsSnapshot {
    refreshCurrentMonthEnrolledAccountsSnapshot {
      status
      message
    }
  }
`;

export type UploadLogoResponse = {
  uploadLogo: {
    message: string;
    etag: string;
    s3Url: string;
  };
};
export const UPLOAD_LOGO = gql`
  mutation UploadLogo($file: Upload!, $relyingPartyId: String!) {
    uploadLogo(file: $file, relyingPartyId: $relyingPartyId) {
      message
      etag
      s3Url
    }
  }
`;

export type UploadAvatarResponse = {
  uploadAvatar: {
    message: string;
    etag: string;
    s3Url: string;
    presignedUrl: string;
  };
};
export const UPLOAD_AVATAR = gql`
  mutation UploadAvatar($file: Upload!, $relyingPartyAdminId: String!) {
    uploadAvatar(file: $file, relyingPartyAdminId: $relyingPartyAdminId) {
      message
      etag
      s3Url
      presignedUrl
    }
  }
`;

export type AccountFollowupSummaryType = {
  totalPatientsCount: number;
  completeCount: number;
  incompleteCount: number;
  totalCount: number;
};

export type GetInitialVisitFollowupsResponse = {
  getAllInitialFollowUps: {
    accountFollowups: FollowUpRemindersTypes[];
    meta: Meta;
    summary: AccountFollowupSummaryType;
  };
};

export const GET_ALL_INITIAL_FOLLOW_UPS = gql`
  query GetAllInitialFollowUps($filterOptions: AccountFollowupFilterOptionsInput, $pageSize: Int, $pageNum: Int) {
    getAllInitialFollowUps(filterOptions: $filterOptions, pageSize: $pageSize, pageNum: $pageNum) {
      accountFollowups {
        id
        followUpOn
        type
        mode
        appointmentRemindersEnabled
        subType
        status
        notes
        relyingParty {
          id
          name
        }
        account {
          truentityId
          rpmStatus
          accountSummary {
            signOffAt
            isTelehealthVisitLaunched
          }
          user {
            firstName
            lastName
          }
          ccmStatus
        }
        clinicalCareEncounter {
          id
          signOffAt
          isTelehealthVisitLaunched
        }
        rpmFollowupConsult {
          id
          signOffAt
          isTelehealthVisitLaunched
        }
        createdAt
      }
      meta {
        totalCount
        totalPages
      }
      summary {
        totalPatientsCount
        completeCount
        incompleteCount
        totalCount
      }
    }
  }
`;

export type GetInitialFollowUpsCountResponse = {
  getAllInitialFollowUps: { meta: { totalCount: number } };
};

/*
 * The same visits Patient Schedules lists, counted only: no rows, so a dashboard card asking "how
 * many" fetches nothing about the patients themselves.
 */
export const GET_INITIAL_FOLLOW_UPS_COUNT = gql`
  query GetInitialFollowUpsCount($filterOptions: AccountFollowupFilterOptionsInput) {
    getAllInitialFollowUps(filterOptions: $filterOptions, pageSize: 1, pageNum: 1) {
      meta {
        totalCount
      }
    }
  }
`;

export type GetAthenaPatientDetails = {
  getAthenaPatientDetails: {
    athenaObjectData: JSONData;
  };
};

export const GET_ATHENA_PATIENT_DETAILS = gql`
  query GetAthenaPatientDetails($truentityId: ID!, $actionType: AthenaPatientInfoEnum!) {
    getAthenaPatientDetails(truentityId: $truentityId, actionType: $actionType) {
      athenaObjectData
    }
  }
`;

export const FETCH_AND_SAVE_ATHENA_PATIENT_DETAILS = gql`
  mutation FetchAndSaveAthenaPatientDetails($truentityId: ID!, $actionType: AthenaPatientInfoEnum!, $claimId: Int) {
    fetchAndSaveAthenaPatientDetails(truentityId: $truentityId, actionType: $actionType, claimId: $claimId) {
      status
      message
      data
    }
  }
`;

export const UPDATE_RPM_PATIENT_ALTERNATE_PROVIDER_ID = gql`
  mutation updateUserAccountProfile($truentityId: String!, $profileData: UserAccountProfileInput!) {
    updateUserAccountProfile(truentityId: $truentityId, profileData: $profileData) {
      account {
        rpmAlternateBillingProvider {
          id
          name
        }
      }
    }
  }
`;

export const TRANSFER_ACCOUNT_FROM_ONE_PHARMACY_TO_ANOTHER = gql`
  mutation SwitchAccountRelyingParty(
    $truentityId: ID!
    $targetRelyingPartyId: ID!
    $targetClientOrgId: ID!
    $targetClientStoreId: ID!
    $newAssigneeId: ID!
  ) {
    switchAccountRelyingParty(
      truentityId: $truentityId
      targetRelyingPartyId: $targetRelyingPartyId
      targetClientOrgId: $targetClientOrgId
      targetClientStoreId: $targetClientStoreId
      newAssigneeId: $newAssigneeId
    ) {
      status
      message
    }
  }
`;

export type GetAccountInsuranceCardResponse = {
  getAccountInsuranceCardImage: {
    insuranceId?: string;
    base64Image: string;
  }[];
};

export const GET_ACCOUNT_INSURANCE_CARD = gql`
  query GetAccountInsuranceImage($truentityId: ID!, $insuranceId: Int) {
    getAccountInsuranceCardImage(truentityId: $truentityId, insuranceId: $insuranceId) {
      base64Image
    }
  }
`;

export type UpdateAccountInsuranceCardImageResponse = {
  updateAccountInsuranceCardImage: {
    status: string;
    message: string;
  };
};

export const UPLOAD_ACCOUNT_INSURANCE_CARD_IMAGE = gql`
  mutation UploadAccountInsuranceCardImage($truentityId: String!, $insuranceId: String!, $image: Upload, $imageKey: String) {
    updateAccountInsuranceCardImage(truentityId: $truentityId, insuranceId: $insuranceId, image: $image, imageKey: $imageKey) {
      status
      message
    }
  }
`;

export const UPDATE_ACCOUNT_INSURANCE_CARD_IMAGE = gql`
  mutation UpdateAccountInsuranceCardImage($truentityId: String!, $insuranceId: String!, $image: Upload, $imageKey: String) {
    updateAccountInsuranceCardImage(truentityId: $truentityId, insuranceId: $insuranceId, image: $image, imageKey: $imageKey) {
      status
      message
    }
  }
`;

export type RemoveInsuranceCardImageResponse = {
  removeInsuranceCardImage: {
    status: string;
    message: string;
  };
};

export const REMOVE_INSURANCE_CARD_IMAGE = gql`
  mutation RemoveInsuranceCardImage($truentityId: String!, $insuranceId: String!) {
    removeInsuranceCardImage(truentityId: $truentityId, insuranceId: $insuranceId) {
      status
      message
    }
  }
`;

export type GetAccountInsuranceDetailsResponse = {
  getAccountInsuranceDetails: JSONData;
};

export const GET_ACCOUNT_INSURANCE_DETAILS = gql`
  query GetAccountInsuranceDetails($truentityId: ID!) {
    getAccountInsuranceDetails(truentityId: $truentityId)
  }
`;

export type GetAndSaveAthenaInsuranceDetailsResponse = {
  getAndSaveAthenaInsuranceDetails: {
    status: string;
    message: string;
    insurances: JSONData;
  };
};

export const GET_AND_SAVE_ATHENA_INSURANCE_DETAILS = gql`
  mutation GetAndSaveAthenaInsuranceDetails($truentityId: ID!, $forceRefresh: Boolean) {
    getAndSaveAthenaInsuranceDetails(truentityId: $truentityId, forceRefresh: $forceRefresh) {
      status
      message
      insurances
    }
  }
`;

export type GetAthenaTopInsurancesResponse = {
  getAthenaTopInsurance: JSONData;
};

export const GET_ATHENA_TOP_INSURANCES = gql`
  query GetAthenaTopInsurance {
    getAthenaTopInsurance
  }
`;

export type AddAthenaInsuranceResponse = {
  addAthenaAccountInsurance?: {
    status: string;
    message: string;
  };
};

export const ADD_ATHENA_INSURANCE = gql`
  mutation AddAthenaAccountInsurance(
    $truentityId: ID!
    $insurancePackageId: String!
    $insurancePhone: String
    $relationshipToInsured: String
    $relationshipToInsuredId: Int
    $sequenceNumber: Int
    $insuranceIdNumber: String!
  ) {
    addAthenaAccountInsurance(
      truentityId: $truentityId
      insurancePackageId: $insurancePackageId
      insurancePhone: $insurancePhone
      relationshipToInsured: $relationshipToInsured
      relationshipToInsuredId: $relationshipToInsuredId
      sequenceNumber: $sequenceNumber
      insuranceIdNumber: $insuranceIdNumber
    ) {
      status
      message
    }
  }
`;

export type AddAthenaDualEligibleInsuranceResponse = {
  addAthenaDualEligibleAccountInsurance?: {
    status: string;
    message: string;
  };
};

export const ADD_ATHENA_DUAL_ELIGIBLE_INSURANCE = gql`
  mutation AddAthenaDualEligibleAccountInsurance(
    $truentityId: ID!
    $insurancePackageId: String!
    $insurancePhone: String
    $sequenceNumber: Int
    $insuranceIdNumber: String!
    $secondaryInsurancePackageId: String!
    $secondaryInsurancePhone: String
    $secondarySequenceNumber: Int
    $secondaryInsuranceIdNumber: String!
  ) {
    addAthenaDualEligibleAccountInsurance(
      truentityId: $truentityId
      insurancePackageId: $insurancePackageId
      insurancePhone: $insurancePhone
      sequenceNumber: $sequenceNumber
      insuranceIdNumber: $insuranceIdNumber
      secondaryInsurancePackageId: $secondaryInsurancePackageId
      secondaryInsurancePhone: $secondaryInsurancePhone
      secondarySequenceNumber: $secondarySequenceNumber
      secondaryInsuranceIdNumber: $secondaryInsuranceIdNumber
    ) {
      status
      message
    }
  }
`;

export type UpdateAthenaInsuranceResponse = {
  updateAthenaAccountInsurance?: {
    status: string;
    message: string;
  };
};

export const UPDATE_ATHENA_INSURANCE = gql`
  mutation UpdateAthenaAccountInsurance(
    $truentityId: ID!
    $insurancePackageId: String!
    $insurancePhone: String
    $relationshipToInsured: String
    $relationshipToInsuredId: Int
    $sequenceNumber: Int
    $insuranceIdNumber: String!
    $insuranceId: String
    $policyHolder: InsurancePolicyHolderInput
  ) {
    updateAthenaAccountInsurance(
      truentityId: $truentityId
      insurancePackageId: $insurancePackageId
      insurancePhone: $insurancePhone
      relationshipToInsured: $relationshipToInsured
      relationshipToInsuredId: $relationshipToInsuredId
      sequenceNumber: $sequenceNumber
      insuranceIdNumber: $insuranceIdNumber
      insuranceId: $insuranceId
      policyHolder: $policyHolder
    ) {
      status
      message
    }
  }
`;

export type DeleteAthenaInsuranceResponse = {
  removeAthenaAccountInsurance?: {
    status: string;
    message: string;
  };
};

export const DELETE_ATHENA_INSURANCE = gql`
  mutation RemoveAthenaAccountInsurance($truentityId: ID!, $insuranceId: String!) {
    removeAthenaAccountInsurance(truentityId: $truentityId, insuranceId: $insuranceId) {
      status
      message
    }
  }
`;

export type SwapInsuranceSequenceResponse = {
  swapInsuranceSequence?: {
    status: string;
    message: string;
  };
};

export const SWAP_INSURANCE_SEQUENCE = gql`
  mutation SwapInsuranceSequence($truentityId: ID!, $insuranceIdA: String!, $insuranceIdB: String!) {
    swapInsuranceSequence(truentityId: $truentityId, insuranceIdA: $insuranceIdA, insuranceIdB: $insuranceIdB) {
      status
      message
    }
  }
`;

export type AthenaInsuranceImportHistoryLog = {
  insurances: JSONData;
  importedAt: Date;
  importedBy: string;
};

export type GetAthenaInsuranceImportHistoryResponse = {
  getAthenaInsuranceImportHistory: AthenaInsuranceImportHistoryLog[];
};

export const GET_ATHENA_INSURANCE_IMPORT_HISTORY = gql`
  query GetAthenaInsuranceImportHistory($truentityId: ID!) {
    getAthenaInsuranceImportHistory(truentityId: $truentityId) {
      insurances
      importedAt
      importedBy
    }
  }
`;

export type GetAthenaInsuranceEligibilityHistoryResponse = {
  getAccountInsuranceEligibilityHistory: {
    serviceName: string;
    triggeredAt: Date;
    triggeredBy: string;
    responseData: JSONData;
  }[];
};

export const GET_ATHENA_INSURANCE_ELIGIBILITY_HISTORY = gql`
  query GetAthenaInsuranceEligibilityHistory($truentityId: ID!, $insuranceId: String) {
    getAccountInsuranceEligibilityHistory(truentityId: $truentityId, insuranceId: $insuranceId) {
      serviceName
      triggeredAt
      triggeredBy
      responseData
    }
  }
`;

export type GetAccountInsuranceBenefitsDetailsResponse = {
  getAccountInsuranceBenefitsDetails: JSONData;
};

export const GET_INSURANCE_BENEFITS_DETAILS = gql`
  query GetAccountInsuranceBenefitsDetails($truentityId: ID!, $insuranceId: String) {
    getAccountInsuranceBenefitsDetails(truentityId: $truentityId, insuranceId: $insuranceId)
  }
`;

export type GenerateRpmPriorityReportResponseType = {
  generateRpmPriorityReport: {
    success: boolean;
    reportUrl: string;
    message: string;
  };
};

export const GENERATE_RPM_PRIORITY_REPORT = gql`
  mutation GenerateRpmPriorityReport($filterOptions: AccountFilterOptionsInput) {
    generateRpmPriorityReport(filterOptions: $filterOptions) {
      success
      reportUrl
      message
    }
  }
`;

export type GetRpmPriorityReportsReponseType = {
  getRpmPriorityReports: Array<{
    filename: string;
    url: string;
    size: number;
    createdAt: Date;
    updatedAt: Date;
    isPlaceholder: boolean;
    status: string;
  }>;
};

export const GET_RPM_PRIORITY_REPORTS = gql`
  query GetRpmPriorityReports($includePlaceholders: Boolean) {
    getRpmPriorityReports(includePlaceholders: $includePlaceholders) {
      filename
      url
      size
      createdAt
      updatedAt
      isPlaceholder
      status
    }
  }
`;

export type GetPatientRpmVisitReportsResponse = {
  accountGet: {
    lastAvailableAccountCareSummary: {
      s3Key: string;
    };
    accountSummary: {
      rpmEncounterS3Key: string;
      rpmCombinedEncounterReportS3Key: string;
    };
  };
};

export const GET_PATIENT_RPM_VISIT_REPORTS = gql`
  query getPatientDetail($truentityId: String!) {
    accountGet(truentityId: $truentityId) {
      lastAvailableAccountCareSummary {
        s3Key
      }
      accountSummary {
        rpmEncounterS3Key
        rpmCombinedEncounterReportS3Key
      }
    }
  }
`;

export const GET_ACCOUNT_SNAPSHOT = gql`
  query GetAccountRpmSnapshot($truentityId: ID!, $monthYear: String!) {
    getAccountRpmSnapshot(truentityId: $truentityId, monthYear: $monthYear) {
      account {
        lastMedrecDate
        lastRpmReportDeliveredAt
        lastRpmReportAttemptedAt
      }
      followupVisitDueStatus
      lastContactedType
      lastContactedDate
    }
  }
`;

export type SendAccountInsuranceEligibilityRequestResponse = {
  sendAccountInsuranceEligibilityRequest: {
    status: string;
    message: string;
  };
};

export const REQUEST_ACCOUNT_INSURANCE_BENEFITS_REQUEST = gql`
  mutation SendAccountInsuranceEligibilityRequest(
    $truentityId: ID!
    $insuranceId: String
    $dateOfService: ISO8601Date
    $serviceTypeCode: String
  ) {
    sendAccountInsuranceEligibilityRequest(
      truentityId: $truentityId
      insuranceId: $insuranceId
      dateOfService: $dateOfService
      serviceTypeCode: $serviceTypeCode
    ) {
      status
      message
    }
  }
`;

export type GetPatientMonitoredConditionsResponse = {
  getAccountMonitoredConditions: {
    medicalSubServices: Array<MedicalSubServiceType>;
  };
};

export const GET_PATIENT_MONITORED_CONDITIONS = gql`
  query GetAccountMonitoredConditions($truentityId: ID!, $medicalServiceNames: [String!]!) {
    getAccountMonitoredConditions(truentityId: $truentityId, medicalServiceNames: $medicalServiceNames) {
      medicalSubServices {
        id
        name
        description
        diagnosisCode
        diagnosisName
        diagnosisId
        createdAt
        updatedAt
      }
    }
  }
`;

export type GetPatientAccessResponse = {
  getAccountAccess: {
    hasAccess: boolean;
    requiresSwitch: boolean;
    message?: string;
    suggestedRelyingPartyUuid?: string;
    suggestedRelyingPartyName?: string;
  };
};

export const GET_PATIENT_ACCESS = gql`
  query GetAccountAccess($truentityId: String!) {
    getAccountAccess(truentityId: $truentityId) {
      hasAccess
      requiresSwitch
      message
      suggestedRelyingPartyUuid
      suggestedRelyingPartyName
    }
  }
`;

export type PlaceManualBillingHoldResponse = {
  placeManualBillingHold: {
    status: string;
    message: string;
  };
};

export const PLACE_MANUAL_BILLING_HOLD = gql`
  mutation PlaceManualBillingHold($truentityId: ID!, $relyingPartyId: ID) {
    placeManualBillingHold(truentityId: $truentityId, relyingPartyId: $relyingPartyId) {
      status
      message
    }
  }
`;

export type ReleaseManualBillingHoldResponse = {
  releaseManualBillingHold: {
    status: string;
    message: string;
  };
};

export const RELEASE_MANUAL_BILLING_HOLD = gql`
  mutation ReleaseManualBillingHold($truentityId: ID!, $relyingPartyId: ID, $releasedFromMonth: String, $note: String) {
    releaseManualBillingHold(
      truentityId: $truentityId
      relyingPartyId: $relyingPartyId
      releasedFromMonth: $releasedFromMonth
      note: $note
    ) {
      status
      message
    }
  }
`;

export const GET_CARE_MANAGEMENT_ENROLLED_ACCOUNTS = gql`
  query GetCareManagementEnrolledAccountsQuery(
    $pageNum: Int!
    $pageSize: Int!
    $filterMonthYear: String!
    $filterByAccountIds: [ID!]
    $filterType: String
    $sortModel: [SortModelInput!]
    $tagFilters: [ID!]
    $filterByPaused: Boolean
    $filterByBillingWarning: Boolean
    $filterByEligibilityUnverified: Boolean
    $filterBySmsOptedOut: Boolean
  ) {
    getRpmEnrolledAccounts: getCareManagementEnrolledAccounts(
      pageNum: $pageNum
      pageSize: $pageSize
      filterMonthYear: $filterMonthYear
      filterType: $filterType
      filterByAccountIds: $filterByAccountIds
      sortModel: $sortModel
      tagFilters: $tagFilters
      filterByPaused: $filterByPaused
      filterByBillingWarning: $filterByBillingWarning
      filterByEligibilityUnverified: $filterByEligibilityUnverified
      filterBySmsOptedOut: $filterBySmsOptedOut
    ) {
      rpmEnrolledAccounts {
        ...RpmSnapshotRowFields
      }
      meta {
        totalPages
        totalCount
      }
      snapshotsRefreshedAt
      lastFullRefreshAt
    }
  }
  ${RPM_SNAPSHOT_ROW_FIELDS}
`;

/*
 * The same enrolled list, cut down to who each row is, which devices they have, and when their last
 * BP, BG and heart-rate readings came in -- what the Patients KPI's Readings Today widget counts and
 * the Device Readings Tracker's BP / BG views split by. The full row fragment would send every
 * patient's name, tags and figures just to be thrown away.
 */
export const GET_CARE_MANAGEMENT_LAST_READINGS = gql`
  query GetCareManagementLastReadingsQuery($pageNum: Int!, $pageSize: Int!, $filterMonthYear: String!, $sortModel: [SortModelInput!]) {
    getRpmEnrolledAccounts: getCareManagementEnrolledAccounts(
      pageNum: $pageNum
      pageSize: $pageSize
      filterMonthYear: $filterMonthYear
      sortModel: $sortModel
    ) {
      rpmEnrolledAccounts {
        account {
          truentityId
        }
        devices
      }
    }
  }
`;

export type GetCareManagementLastReadingsResponse = {
  getRpmEnrolledAccounts: {
    rpmEnrolledAccounts: {
      account: { truentityId: string };
      // The snapshot's connected devices: [{ monitoring_device: { type_name: 'bpm' | 'glucometer' | ... } }].
      devices: null | JSONData;
    }[];
  };
};

export const GET_CARE_MANAGEMENT_UNENROLLED_ACCOUNTS = gql`
  query GetCareManagementUnenrolledAccountsQuery($pageNum: Int, $pageSize: Int, $filterOptions: AccountFilterOptionsInput!) {
    getAccountsByRpmStatus: getCareManagementUnenrolledAccounts(pageNum: $pageNum, pageSize: $pageSize, filterOptions: $filterOptions) {
      accounts {
        id
        truentityId
        phone
        gender
        rpmEnrolledAt
        rpmUnenrolledAt
        lastMedrecDate
        rpmApprovalStatus
        rpmSignOffStatus
        onboardedAt
        rpmStatus
        ccmStatus
        clinicalCareEncounterMode
        healthPlan {
          orgName
        }
        birthDate
        zipcode
        setting
        doNotCall
        user {
          firstName
          lastName
          verifiedEmail
        }
        tags {
          id
          name
          colorCode
        }
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

// Live month-to-date page time and billable care time on a patient, by pharmacy and by user.
// Polled while the patient page is open; see usePatientEngagementTime.
export const GET_PATIENT_ENGAGEMENT_TIME_SUMMARY = gql`
  query PatientEngagementTimeSummary($patientId: String!) {
    patientEngagementTimeSummary(patientId: $patientId) {
      monthStart
      monthEnd
      asOf
      totals {
        pageSecs
        rpmCareSecs
        ccmCareSecs
      }
      pharmacies {
        relyingParty {
          id
          name
        }
        isCurrent
        pageSecs
        rpmCareSecs
        ccmCareSecs
      }
      users {
        relyingPartyAdmin {
          id
          roleType
          isObserver
          user {
            firstName
            lastName
          }
        }
        relyingPartyId
        pageSecs
        rpmCareSecs
        ccmCareSecs
        isActiveNow
      }
    }
  }
`;

export const GET_ACCOUNTS_SHARING_PHONE = gql`
  query getAccountsSharingPhone($truentityId: String!) {
    accountGet(truentityId: $truentityId) {
      id
      accountsSharingPhone {
        accounts {
          truentityId
          fullName
        }
        otherPharmacyCount
      }
    }
  }
`;

export type GetAccountsSharingPhoneResponse = {
  accountGet: { id: string; accountsSharingPhone: AccountsSharingPhone | null } | null;
};

export const RESTART_ACCOUNT_TEXT_MESSAGES = gql`
  mutation restartAccountTextMessages(
    $truentityId: String!
    $consentedOn: ISO8601Date!
    $consentMethod: SmsConsentMethodTypeEnum!
    $patientAgreed: Boolean!
  ) {
    restartAccountTextMessages(
      truentityId: $truentityId
      consentedOn: $consentedOn
      consentMethod: $consentMethod
      patientAgreed: $patientAgreed
    ) {
      status
      message
    }
  }
`;
